// ────────────────────────────────────────────────────────────────────────
// IconPicker — selector de tipo de vehículo para un dispositivo GPS.
//
// Botón con el ícono lucide del tipo actual; al hacer click abre un popover
// con la grilla de tipos disponibles. Click en uno persiste el override y
// dispara el evento global `gps-icon-change` (mapa/lista reaccionan en caliente).
// Click en el actual o en "Resetear" vuelve al tipo original del device.
// ────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, RotateCcw } from 'lucide-react';
import { VEHICLE_TYPES, setVehicleType, useVehicleType } from '../vehicleTypes';
import { cn } from '../../../../utils/cn';

export default function IconPicker({ device, size = 'md' }) {
  const { t } = useTranslation();
  const type = useVehicleType(device);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const Trigger = type.Lucide;

  // Cerrar al click afuera o ESC.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const dim = size === 'sm' ? 'w-7 h-7' : 'w-8 h-8';
  const iconDim = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  const choose = (id) => {
    setVehicleType(device.id, id === device.tipo ? null : id);
    setOpen(false);
  };
  const reset = () => {
    setVehicleType(device.id, null);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title={t('Cambiar ícono del vehículo') + ' — ' + t(type.label)}
        aria-label={t('Cambiar ícono del vehículo')}
        className={cn(
          'inline-flex items-center justify-center rounded-lg border border-border bg-bg text-fg-muted hover:text-fg hover:border-brand-400 hover:bg-bg-muted transition-colors',
          dim,
        )}
      >
        <Trigger className={iconDim} aria-hidden="true" />
      </button>
      {open && (
        // popover, not modal; ui/Modal would be heavy here
        <div
          role="dialog"
          className="absolute z-50 mt-1 right-0 w-60 rounded-xl border border-border bg-bg shadow-lg p-2 anim-fade-in-up"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
            {t('Tipo de vehículo')}
          </div>
          <ul className="grid grid-cols-3 gap-1 p-1">
            {VEHICLE_TYPES.map((vt) => {
              const isActive = vt.id === type.id;
              const Icon = vt.Lucide;
              return (
                <li key={vt.id}>
                  <button
                    type="button"
                    onClick={() => choose(vt.id)}
                    title={t(vt.label)}
                    className={cn(
                      'group w-full flex flex-col items-center gap-1 px-1.5 py-2 rounded-lg border text-[10px] font-medium transition-colors',
                      isActive
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-transparent text-fg-muted hover:bg-bg-muted hover:text-fg',
                    )}
                  >
                    <Icon className="w-5 h-5" aria-hidden="true" />
                    <span className="truncate w-full text-center">{t(vt.label)}</span>
                    {isActive && <Check className="absolute opacity-0 w-3 h-3" aria-hidden="true" />}
                  </button>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            onClick={reset}
            className="w-full mt-1 inline-flex items-center justify-center gap-1.5 h-8 rounded-md text-xs text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors"
          >
            <RotateCcw className="w-3 h-3" />
            {t('Volver al tipo original')}
          </button>
        </div>
      )}
    </div>
  );
}
